import { useId } from "react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid, Legend, Cell,
} from "recharts";
import { compact, currency } from "@/lib/format";
import { CHART_PALETTE } from "@/lib/chart-theme";
import { PlatformLogo } from "@/components/ui/platform-logo";

/** shadcn-style tooltip card: date heading + one colored row per series. */
function SeriesTooltip({ active, payload, label, series, moneyFmt }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl border border-border bg-popover px-3 py-2 text-xs shadow-lg">
      <p className="mb-1.5 font-medium text-popover-foreground">{label}</p>
      <div className="space-y-1">
        {payload.map((p: any) => {
          const s = series?.find((x: SeriesDef) => x.name === p.name);
          const v = Number(p.value);
          const val = s?.kind === "currency" ? (moneyFmt ? moneyFmt(v) : currency(v)) : compact(v);
          return (
            <div key={p.name} className="flex items-center justify-between gap-6">
              <span className="flex items-center gap-1.5 text-muted-foreground">
                <span className="h-2 w-2 rounded-full" style={{ background: p.color ?? p.stroke }} />
                {p.name}
              </span>
              <span className="font-medium tabular-nums text-popover-foreground">{val}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Axis tick that stacks a brand logo above the category label. */
function LogoTick({ x, y, payload, logoFor }: any) {
  const info = logoFor?.(payload?.value);
  return (
    <g transform={`translate(${x},${y})`}>
      <foreignObject x={-44} y={8} width={88} height={48}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
          {info && <PlatformLogo platform={info.platform} accentColor={info.accent} size={18} />}
          <span style={{ fontSize: 11, color: "var(--muted-foreground)", whiteSpace: "nowrap" }}>{payload?.value}</span>
        </div>
      </foreignObject>
    </g>
  );
}

const AXIS = { fontSize: 11, fill: "var(--muted-foreground)" } as const;
const GRID = "rgb(0 0 0 / 0.045)";

export interface SeriesDef {
  dataKey: string;
  name: string;
  color: string;
  /** "currency" formats the axis/tooltip with €; otherwise compact numbers. */
  kind?: "currency" | "number";
  axis?: "left" | "right";
}

/** Drop the trailing incomplete edge of a daily series so the line doesn't cliff
 *  to zero: today's still-syncing day, any fully-empty tail, and one final
 *  partial day (a sharp drop vs the prior day = data hadn't fully landed). */
function trimTrailingEmpty(data: any[], keys: string[]): any[] {
  const today = new Date().toISOString().slice(0, 10);
  const sum = (row: any) => keys.reduce((s, k) => s + (Number(row?.[k]) || 0), 0);
  let end = data.length;
  while (end > 1) {
    const row = data[end - 1];
    if (keys.every((k) => !row?.[k]) || row?.date === today) end--;
    else break;
  }
  // one trailing partial day: <40% of the previous day's total
  if (end > 2) {
    const prev = sum(data[end - 2]);
    if (prev > 0 && sum(data[end - 1]) < prev * 0.4) end--;
  }
  return end === data.length ? data : data.slice(0, end);
}

/** Stacked/overlaid area chart over a date series. `moneyFmt` (from useMoney)
 *  formats currency-kind series in the active display currency. */
export function TrendArea({ data, series, moneyFmt }: { data: any[]; series: SeriesDef[]; moneyFmt?: (n: number) => string }) {
  const hasRight = series.some((s) => s.axis === "right");
  data = trimTrailingEmpty(data, series.map((s) => s.dataKey));
  return (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart data={data} accessibilityLayer margin={{ top: 6, right: 8, left: 0, bottom: 0 }}>
        <defs>
          {series.map((s) => (
            <linearGradient key={s.dataKey} id={`g-${s.dataKey}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={s.color} stopOpacity={0.5} />
              <stop offset="95%" stopColor={s.color} stopOpacity={0.03} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="4 4" stroke={GRID} vertical={false} />
        <XAxis dataKey="date" tick={AXIS} tickFormatter={(d: string) => d.slice(5)} minTickGap={28} tickLine={false} axisLine={false} tickMargin={10} />
        <YAxis yAxisId="left" tick={AXIS} tickFormatter={(v: number) => compact(v)} width={40} tickLine={false} axisLine={false} tickCount={5} />
        {hasRight && <YAxis yAxisId="right" orientation="right" tick={AXIS} tickFormatter={(v: number) => compact(v)} width={40} tickLine={false} axisLine={false} tickCount={5} />}
        <Tooltip cursor={{ stroke: "var(--border)", strokeWidth: 1 }} content={<SeriesTooltip series={series} moneyFmt={moneyFmt} />} />
        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} iconType="circle" />
        {series.map((s) => (
          <Area
            key={s.dataKey}
            yAxisId={s.axis === "right" ? "right" : "left"}
            type="monotone"
            dataKey={s.dataKey}
            name={s.name}
            stroke={s.color}
            strokeWidth={2}
            fill={`url(#g-${s.dataKey})`}
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: "var(--background)" }}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
}

/** Horizontal-ish category bar chart (one metric across a breakdown dimension). */
export function CategoryBars({
  data, dataKey, kind = "number", colors, moneyFmt, logoFor,
}: {
  data: any[];
  dataKey: string;
  kind?: "currency" | "number";
  colors?: string[];
  moneyFmt?: (n: number) => string;
  /** When set, renders a brand logo above each category label. */
  logoFor?: (label: string) => { platform: string; accent?: string } | undefined;
}) {
  const uid = useId();
  const palette = colors ?? CHART_PALETTE;
  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data} accessibilityLayer margin={{ top: 6, right: 8, left: 0, bottom: logoFor ? 30 : 0 }}>
        <defs>
          {data.map((_, i) => {
            const c = palette[i % palette.length];
            return (
              <linearGradient key={i} id={`${uid}-bar-${i}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={c} stopOpacity={0.95} />
                <stop offset="100%" stopColor={c} stopOpacity={0.4} />
              </linearGradient>
            );
          })}
        </defs>
        <CartesianGrid strokeDasharray="4 4" stroke={GRID} vertical={false} />
        <XAxis dataKey="label" tick={logoFor ? (p: any) => <LogoTick {...p} logoFor={logoFor} /> : AXIS} interval={0} height={logoFor ? 52 : 30} tickLine={false} axisLine={false} tickMargin={10} />
        <YAxis tick={AXIS} tickFormatter={(v: number) => compact(v)} width={40} tickLine={false} axisLine={false} tickCount={5} />
        <Tooltip
          cursor={{ fill: "rgb(0 0 0 / 0.04)" }}
          content={({ active, payload, label }: any) => {
            if (!active || !payload?.length) return null;
            const v = Number(payload[0].value);
            const val = kind === "currency" ? (moneyFmt ? moneyFmt(v) : currency(v)) : compact(v);
            return (
              <div className="rounded-xl border border-border bg-popover px-3 py-2 text-xs shadow-lg">
                <p className="font-medium text-popover-foreground">{label}</p>
                <p className="mt-0.5 tabular-nums text-muted-foreground">{val}</p>
              </div>
            );
          }}
        />
        <Bar dataKey={dataKey} radius={[6, 6, 0, 0]}>
          {data.map((_, i) => (
            <Cell key={i} fill={`url(#${uid}-bar-${i})`} stroke={palette[i % palette.length]} strokeOpacity={0.25} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
